import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CONSENT_EVENT,
  fbTrack,
  hasMarketingConsent,
  initMetaPixel,
} from '@/lib/metaPixel';

/**
 * Meta Pixel SPA tracker.
 *
 * The pixel is loaded only after the visitor accepts marketing cookies.
 * `initMetaPixel()` fires the first PageView, so this component tracks a
 * PageView only when the location actually changes to a different URL —
 * never twice for the same path (Meta Pixel Helper duplicate warning).
 */
export default function MetaPixel() {
  const { pathname, search } = useLocation();
  const lastTracked = useRef<string | null>(null);

  useEffect(() => {
    const key = `${pathname}${search}`;

    const sync = () => {
      if (!hasMarketingConsent()) return;
      const firstLoad = lastTracked.current === null;
      initMetaPixel(); // no-op once already loaded
      if (firstLoad) {
        // The init call above (or the previous session's init) already sent
        // the PageView for this URL.
        lastTracked.current = key;
        return;
      }
      if (lastTracked.current !== key) {
        fbTrack('PageView');
        lastTracked.current = key;
      }
    };

    sync();
    window.addEventListener(CONSENT_EVENT, sync);
    return () => window.removeEventListener(CONSENT_EVENT, sync);
  }, [pathname, search]);

  return null;
}
